import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import ContainerBox from 'components/ui/ContainerBox';
import { useApolloClient } from '@apollo/react-hooks';
import { Option } from '../../../../ui/FormControls';
import {
    useFormControl,
    useFormGroup,
    useManageTemplateForm,
} from '../../../../../utils/form.utils';
import { requiredValidator } from '../../../../../utils/validator.utils';
import ManageTemplate, {
    ManageTemplateInstance,
} from '../../../../ui/ManageTemplate';
import {
    ADD_USER_GUIDE_SCOPE,
    DELETE_USER_GUIDE_SCOPE,
    EDIT_USER_GUIDE_SCOPE,
} from '../../../../../gql/userGuideScopes';
import { UserGuideScope } from '../../../../../shared/types';
import { getUserGuideScopeSelectionOptions } from '../../../../../utils/user-guide.utils';
import { useNotification } from '../../../../providers/NotificationProvider';
import { returnListUrl } from '../constants';
import useUserGuideScopeActions from '../hooks/useUserGuideScopeActions';
import UserGuideScopeFormFields from './UserGuideScopeFormFields';

export type UserGuideScopeOptionsData = {
    availableNeonScopes?: string[];
    userGuideScopes?: UserGuideScope[];
};

interface UserGuideScopeFormProps {
    document?: UserGuideScope;
    editGroupItems: UserGuideScope[];
    optionsData?: UserGuideScopeOptionsData;
    initialScopes: string[];
    scopeOptionsFailed: boolean;
}

const UserGuideScopeForm = ({
    document,
    editGroupItems,
    optionsData,
    initialScopes,
    scopeOptionsFailed,
}: UserGuideScopeFormProps): JSX.Element => {
    const form = useFormGroup({
        scope: useFormControl<string[]>(initialScopes, [requiredValidator]),
        name: useFormControl<string>(document?.name ?? '', [requiredValidator]),
    });
    const apolloClient = useApolloClient();
    const { showMessage } = useNotification();

    const duplicateNameError = useMemo(() => {
        const currentName = (form.name.value || '').trim();

        if (!currentName || currentName === document?.name) {
            return '';
        }

        const isDuplicate = (optionsData?.userGuideScopes ?? []).some(
            (scope) => scope.name === currentName
        );

        return isDuplicate
            ? 'This name has already been used. Please choose a different name.'
            : '';
    }, [document?.name, form.name.value, optionsData?.userGuideScopes]);

    const scopeSelectionOptions = useMemo(
        () =>
            getUserGuideScopeSelectionOptions(
                optionsData?.availableNeonScopes,
                optionsData?.userGuideScopes,
                form.scope.value
            ),
        [
            form.scope.value,
            optionsData?.availableNeonScopes,
            optionsData?.userGuideScopes,
        ]
    );
    const scopeOptions: Option[] = scopeSelectionOptions.map((option) => ({
        label: option.label,
        value: option.value,
        secondaryLabel: option.secondaryLabel,
        disabled: option.disabled,
        scopes: option.scopes,
        groupLabel: option.groupLabel,
    }));
    const scopeValuesByOptionValue = new Map(
        scopeSelectionOptions.map((option) => [option.value, option.scopes])
    );
    const selectedScopeOptions: Option[] = scopeSelectionOptions
        .filter((option) =>
            option.scopes.every((scope) => form.scope.value.includes(scope))
        )
        .map((option) => ({
            label: option.label,
            value: option.value,
        }));
    const isPermissionOption = (option: Option) =>
        (option.scopes as string[]).some((scope) => scope.includes(':'));
    const neonAccessOptions = scopeOptions.filter(
        (option) => !isPermissionOption(option)
    );
    const permissionOptions = scopeOptions.filter(isPermissionOption);
    const selectedNeonAccessOptions = selectedScopeOptions.filter(
        (option) => !option.value.toString().includes(':')
    );
    const selectedPermissionOptions = selectedScopeOptions.filter((option) =>
        option.value.toString().includes(':')
    );

    const isFormValid = useMemo(() => {
        const name = (form.name.value || '').trim();
        const selectedScopes = (form.scope.value || []).filter(Boolean);
        return (
            form.isValid &&
            name.length > 0 &&
            selectedScopes.length > 0 &&
            !duplicateNameError
        );
    }, [form.isValid, form.name.value, form.scope.value, duplicateNameError]);

    const {
        isNew,
        formMessage,
        formBusy,
        onCancel,
        onAlertBoxClose,
        updateFormMessage,
    } = useManageTemplateForm(
        ManageTemplateInstance.UserGuideScope,
        form,
        document,
        ADD_USER_GUIDE_SCOPE,
        EDIT_USER_GUIDE_SCOPE,
        DELETE_USER_GUIDE_SCOPE,
        returnListUrl
    );

    const { localBusy, saveScopes, deleteScopeGroup } =
        useUserGuideScopeActions({
            apolloClient,
            form: {
                isValid: form.isValid,
                setDirty: form.setDirty,
                name: {
                    value: form.name.value,
                },
                scope: {
                    value: form.scope.value,
                },
            },
            isFormValid,
            isNew,
            editGroupItems,
            document,
            onCancel,
            showMessage,
            showErrorBanner: updateFormMessage,
        });
    const displayedFormMessage = scopeOptionsFailed
        ? {
              style: 'error' as const,
              message: 'Unable to load the Auth0 scope list. Please try again.',
          }
        : formMessage;

    return (
        <ContainerBox>
            <Box sx={{ flex: 1, flexDirection: 'column', maxWidth: 1200 }}>
                <ManageTemplate
                    dialogMessage={
                        <>
                            You are about to delete the user guide scope mapping{' '}
                            <strong>{` ${form.name.value}`}</strong>. This
                            action cannot be undone, so you must be sure and
                            confirm.
                        </>
                    }
                    onDelete={deleteScopeGroup}
                    pathArea={'User Guide'}
                    returnUrl={returnListUrl}
                    templateInstance={ManageTemplateInstance.UserGuideScope}
                    isNew={isNew}
                    formMessage={displayedFormMessage}
                    formBusy={formBusy || localBusy}
                    onCancel={onCancel}
                    onSubmit={saveScopes}
                    onAlertBoxClose={onAlertBoxClose}
                    templateCreateButtonLabel="Add scope"
                    templateUpdateButtonLabel="Save changes"
                    templateDeleteButtonLabel="Delete scope"
                    titleOverride={
                        isNew ? 'Add user guide scope' : 'Edit user guide scope'
                    }
                >
                    <UserGuideScopeFormFields
                        nameField={form.name}
                        scopeField={form.scope}
                        duplicateNameError={duplicateNameError}
                        neonAccessOptions={neonAccessOptions}
                        selectedNeonAccessOptions={selectedNeonAccessOptions}
                        permissionOptions={permissionOptions}
                        selectedPermissionOptions={selectedPermissionOptions}
                        scopeValuesByOptionValue={scopeValuesByOptionValue}
                    />
                </ManageTemplate>
            </Box>
        </ContainerBox>
    );
};

export default UserGuideScopeForm;
